import { Engine } from 'json-rules-engine';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertTitle, Typography } from '@mui/material';
import * as yup from 'yup';
import * as customComponents from '@product-framework/components/FormFields';
import {
  useDataProvider,
  useLocalization,
  useMasterData,
  useURLParamsDynamically,
  useUserAccess,
  LoadingEvent,
  useSnackbar,
} from '@ui-framework/index';
import SJFormHelper from './SJFormHelper';
import SJFormUI from './SJFormUI';
import { AiSentimentProvider } from '../ArtificalIntelligence/AiSentimentProvider';
import { ServiceModuleProvider } from '../ServiceModuleProvider';
import formButtonsSchemaConfig from '../../../config/formSchemaButtons';

const JOB_AVAILABILITY = {
  JOB_NOT_FOUND_FOR_ACCEPTANCE: 'JOB_NOT_FOUND_FOR_ACCEPTANCE',
  JOB_NOT_FOUND_NOT_AVAILABLE: 'JOB_NOT_FOUND_NOT_AVAILABLE',
  JOB_AVAILABLE: 'JOB_AVAILABLE',
  API_ERROR: 'JOB_NOT_FOUND_API_ERROR',
};

function SJForm({
  formSchema = null,
  action = null,
  onReady = () => undefined,
}) {
  const { formatMessage } = useLocalization();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);
  const snackbar = useSnackbar();
  const userAccess = useUserAccess();

  const [currentPageUrlParams] = useSearchParams();
  const dialogAction = currentPageUrlParams.get('email_action') || null;

  const navigate = useNavigate();
  const urlParams = useURLParamsDynamically();

  const engine = new Engine();
  const masterData = useMasterData();

  const sjFormHelper = new SJFormHelper({
    navigate,
    dataProvider,
    yup,
    formatMessage,
    engine,
    masterData,
  });

  const [actionErrors, setActionErrors] = useState([]);
  const [apiErrors, setApiErrors] = useState([]);

  const initialFormObject = {
    schema: null,
    context: null,
    jobAvailability: JOB_AVAILABILITY.JOB_AVAILABLE,
    formNotFound: false,
    claimUiSchema: {},
  };

  const [formObject, setFormObject] = useState(initialFormObject);
  const [serviceJobFormData, setServiceJobFormData] = useState(null);

  const fetchServiceJob = async () => {
    const spPendingAcceptJobDetailUrl = `/sxcl/v1/service-providers/${urlParams?.spId}/pending-acceptance/{id}`;
    const allServiceJobDetailUrl = '/sxcl/v1/service-jobs/{id}';

    const apiResponse = await sjFormHelper.call({
      method: 'GET',
      url:
        urlParams?.status === 'pendingacceptancejob'
          ? spPendingAcceptJobDetailUrl
          : allServiceJobDetailUrl,
      params: urlParams,
    });

    return apiResponse;
  };

  const fetchServiceJobForm = async data => {
    const apiResponse = await sjFormHelper.call({
      method: `GET`,
      url: '/sxcl/v1/service-job-forms/{tempServiceJobFormId}',
      params: {
        data,
        tempServiceJobFormId: data?.serviceJobForm?.id,
      },
    });

    if (apiResponse?.ok) {
      return { response: apiResponse.data, errors: null };
    }
    snackbar.showMessage({
      type: 'error',
      data: sjFormHelper.formatMessage({
        id: 'SERVICE_JOB_FETCH_ERROR',
        defaultMessage: 'Error fetching service job form',
      }),
    });
    return { response: null, errors: apiResponse.errors };
  };

  const injectPrototypeScheduleButton = buttons => {
    const configEnabled =
      masterData?.applicationFeatureDemo?.dispatchCalendar || false;
    if (!Array.isArray(buttons)) {
      return buttons;
    }
    if (configEnabled) {
      const anchor = 'SERVICE_JOB_DISPATCH_BUTTON';
      const insert = 'SERVICE_JOB_SCHEDULE_DISPATCH_BUTTON';
      const idx = buttons.indexOf(anchor);
      if (idx === -1) {
        return buttons;
      }
      return [...buttons.slice(0, idx), insert, ...buttons.slice(idx)];
    }
    return buttons;
  };

  const injectFormButtonSchema = buttonsSchema => {
    const buttonArray = injectPrototypeScheduleButton(buttonsSchema);
    const newButtonsSchema = buttonArray.reduce((acc, buttonSchema) => {
      acc.push(formButtonsSchemaConfig[buttonSchema]);
      return acc;
    }, []);
    return {
      BUTTONS: newButtonsSchema,
    };
  };

  const setUpFormData = async () => {
    setApiErrors([]);
    if (action === 'CREATE') {
      const createFormObject = {
        schema: formSchema,
        context: {},
        jobAvailability: JOB_AVAILABILITY.JOB_AVAILABLE,
        formNotFound: false,
        claimUiSchema: {},
      };
      setFormObject(createFormObject);
    } else {
      const apiResponse = await fetchServiceJob();

      if (apiResponse?.ok) {
        const serviceJob = apiResponse?.data;
        onReady(serviceJob);

        const apiServiceJobErrors = apiResponse?.errors;

        if (!serviceJob?.serviceJobNumber) {
          sjFormHelper.navigate({
            url: '/servicejobs/preregister',
            params: serviceJob,
          });
        } else {
          if (action === 'REGISTER') {
            const registerFormObject = {
              schema: formSchema,
              context: serviceJob,
              jobAvailability: JOB_AVAILABILITY.JOB_AVAILABLE,
              formNotFound: false,
              claimUiSchema: {},
            };

            setFormObject(registerFormObject);
            if (apiServiceJobErrors) {
              setActionErrors(apiServiceJobErrors);
            }
          }

          let forceProceedToEditFlow = false;

          const jobAssignedToMe =
            userAccess?.isSA() ||
            userAccess?.isAdmin() ||
            serviceJob?.serviceProvider?.id === userAccess?.company?.id;

          // Coming from email
          if (action === 'EMAIL') {
            if (jobAssignedToMe) {
              window.location.href = `/servicejobs/edit/${serviceJob.id}`;
            } else if (serviceJob.serviceJobStatus !== 'DISPATCH_IN_PROCESS') {
              setFormObject({
                ...formObject,
                jobAvailability: JOB_AVAILABILITY.JOB_NOT_FOUND_FOR_ACCEPTANCE,
              });
              snackbar.showMessage({
                type: 'error',
                data: sjFormHelper.formatMessage({
                  id: 'SERVICE_JOB_NOT_AVAILABLE_FOR_ACCEPTANCE',
                  defaultMessage: 'Error displaying job details',
                }),
              });
            } else {
              forceProceedToEditFlow = true;
            }
          }

          if (action === 'EDIT' || forceProceedToEditFlow) {
            const {
              response: serviceJobForm,
              errors: apiServiceJobFormErrors,
            } = await fetchServiceJobForm(serviceJob);

            const buttonSchema = injectFormButtonSchema(
              serviceJobForm.formUiSchema?.BUTTONS,
            );

            const uiSchema = {
              ...serviceJobForm.formUiSchema,
              ...buttonSchema,
            };

            if (serviceJobForm?.formUiSchema) {
              setFormObject({
                schema: uiSchema,
                context: serviceJob,
                jobAvailability: JOB_AVAILABILITY.JOB_AVAILABLE,
                formNotFound: false,
                claimUiSchema: serviceJobForm.claimUiSchema || {},
              });
              setServiceJobFormData(serviceJobForm);
              if (apiServiceJobFormErrors) {
                setActionErrors(apiServiceJobFormErrors);
              }
            } else {
              snackbar.showMessage({
                type: 'error',
                data: sjFormHelper.formatMessage({
                  id: 'SERVICE_JOB_FORM_FETCH_ERROR',
                  defaultMessage: 'Error fetching job form',
                }),
              });
              setFormObject({
                ...formObject,
                formNotFound: true,
              });
            }
          }
        }
      } else {
        snackbar.showMessage({
          type: 'error',
          data: sjFormHelper.formatMessage({
            id: 'SERVICE_JOB_NOT_AVAILABLE',
            defaultMessage: 'Error displaying job details',
          }),
        });
        setApiErrors(apiResponse?.errors);
        setFormObject({
          ...formObject,
          jobAvailability: JOB_AVAILABILITY.API_ERROR,
        });
      }
    }
  };

  useEffect(() => {
    setUpFormData();
  }, [action, urlParams?.spId, urlParams?.id, dialogAction]);

  if (actionErrors && actionErrors.length > 0) {
    return (
      <Alert sx={{ mb: 2, mt: -1 }} severity="error">
        <AlertTitle>
          {formatMessage({
            id: 'FORM_ERRORS',
            defaultMessage: 'Form Errors',
          })}
        </AlertTitle>
        <ul>
          {actionErrors.map(actionError => (
            <li key={actionError?.message}>{actionError?.message}</li>
          ))}
        </ul>
      </Alert>
    );
  }

  if (Array.isArray(apiErrors) && apiErrors.length > 0) {
    return (
      <Alert sx={{ mb: 2, mt: -1 }} severity="error">
        <AlertTitle>
          {formatMessage({
            id: 'JOB_ERRORS',
            defaultMessage: 'Job Error',
          })}
        </AlertTitle>
        <ul>
          {apiErrors.map(apiError => (
            <li key={apiError?.message}>{apiError?.message}</li>
          ))}
        </ul>
      </Alert>
    );
  }

  if (formObject?.formNotFound) {
    return (
      <Alert sx={{ mb: 2 }} severity="error">
        <AlertTitle>
          {formatMessage({
            id: 'JOB_NOT_FOUND',
            defaultMessage: 'Form Not Found',
          })}
        </AlertTitle>
        <Typography variant="body1" mt={2}>
          The job that your are trying to access has no form associated with it.
          Please reach out to support for further help.
        </Typography>
      </Alert>
    );
  }

  if (!formObject?.schema) {
    return <LoadingEvent>...loading Job</LoadingEvent>;
  }

  return (
    <AiSentimentProvider
      serviceJobId={formObject?.context?.id}
      jobStatus={formObject?.context?.serviceJobStatus || 'INCOMPLETE'}>
      <ServiceModuleProvider>
        <SJFormUI
          customComponents={customComponents}
          formObject={formObject}
          serviceJobFormData={serviceJobFormData}
          urlParams={urlParams}
          action={action}
        />
      </ServiceModuleProvider>
    </AiSentimentProvider>
  );
}

export default SJForm;
